import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { saveEstimate } from '../../../../services/estimateService';
import { Download, MapPin, Building2, Ruler } from 'lucide-react';
import { StepContainer } from '../StepContainer';
import { DetailedPriceRange } from '../../../../utils/detailedestimation/calculator';
import { LoadingSpinner } from '../../../ui/LoadingSpinner';
import ErrorMessage from '../../../ui/ErrorMessage';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const PROPERTY_TYPE_TRANSLATIONS = {
  'house': 'Maison',
  'apartment': 'Appartement', 
  'building': 'Immeuble'
} as const;

interface PropertyData {
  location: {
    postcode: string;
    city: string;
    address: string;
  };
  propertyType: string | null;
  area: number;
}

const TVA_RATE = 0.10; // 10% TVA

export const ResultStep: React.FC<{
  data: any;
  estimate: Promise<DetailedPriceRange>;
  onBack: () => void;
  onReset?: () => void;
}> = ({ 
  data = {},
  estimate = Promise.resolve({ min: 0, max: 0, sections: [] }),
  onBack = () => {},
  onReset = () => {},
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculatedEstimate, setCalculatedEstimate] = useState<DetailedPriceRange | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadEstimate = async () => {
      try {
        setLoading(true);
        const result = await estimate;
        setCalculatedEstimate(result);
      } catch (err) {
        console.error('Erreur lors du calcul:', err);
        setError('Impossible de calculer l\'estimation');
      } finally {
        setLoading(false);
      }
    };

    loadEstimate();
  }, [estimate]);

  const handleSave = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    try {
      setSaving(true);
      await saveEstimate(user.uid, {
        ...data,
        type: 'detailed',
        priceRange: calculatedEstimate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de l\'estimation');
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price: number, includeTVA: boolean = true) => {
    const finalPrice = includeTVA ? price * (1 + TVA_RATE) : price;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(finalPrice)
      .replace(/\s/g, '\u00A0'); // Remplace l'espace classique par un espace insécable
  };

const handleExport = () => {
  if (!calculatedEstimate) return;

  const doc = new jsPDF();
  
  // Définition des couleurs et styles
  const primaryColor = [45, 27, 105]; // Bleu SimulReno
  const secondaryColor = [60, 60, 60]; // Gris foncé
  const highlightColor = [200, 50, 50]; // Rouge
  const backgroundGray = [245, 245, 245]; // Gris clair

  // **En-tête**
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text('SimulReno', 15, 18);
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text('DEVIS ESTIMATIF DE TRAVAUX', 15, 25);
  
  // ✅ **Date et N° de devis bien visibles**
  doc.setTextColor(255, 255, 255);  // Texte blanc
  doc.setFontSize(12);
  doc.text(`Date : ${new Date().toLocaleDateString('fr-FR')}`, 150, 18);
  doc.text(`DEVIS N° ${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`, 150, 25);

  // Informations du bien
  doc.setFontSize(14);
  doc.setTextColor(...primaryColor);
  doc.text('Informations du bien', 15, 40);
  doc.setFontSize(11);
  doc.setTextColor(...secondaryColor);
  const infoY = 48;
  doc.text(`Adresse : ${data?.description?.location?.address || 'Non renseigné'}`, 15, infoY);
  doc.text(`Code postal : ${data?.description?.location?.postcode || 'N/A'} ${data?.description?.location?.city || ''}`, 15, infoY + 6);

  // ✅ **Correction : Affichage du type de logement en français**
  const translatedPropertyType = data?.description?.propertyType 
    ? PROPERTY_TYPE_TRANSLATIONS[data.description.propertyType as keyof typeof PROPERTY_TYPE_TRANSLATIONS] 
    : 'Non renseigné';

  doc.text(`Type : ${translatedPropertyType}`, 15, infoY + 12);
  doc.text(`Surface : ${data?.description?.area ? `${data.description.area} m²` : 'Non renseigné'}`, 15, infoY + 18);

  // Séparation visuelle
  doc.setDrawColor(220, 220, 220);
  doc.line(15, 70, 195, 70);

  // Détail des sections
  let yPosition = 80;
  calculatedEstimate.sections.forEach(section => {
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    // Titre de section
    doc.setFillColor(...primaryColor);
    doc.setTextColor(255, 255, 255);
    doc.rect(15, yPosition, 180, 8, 'F');
    doc.setFontSize(11);
    doc.text(section.title.toUpperCase(), 20, yPosition + 5);
    yPosition += 12;

    // Tableau des prestations
    const tableData = section.items.map(item => [
      item.label,
      item.details || '',
      formatPrice(item.price) + ' HT'
    ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Désignation', 'Détails', 'Montant HT']],
      body: tableData,
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 70 },
        2: { cellWidth: 40, halign: 'right' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // Total de section
    doc.setFontSize(11);
    doc.setTextColor(...primaryColor);
    doc.setFont(undefined, 'bold');
    doc.text(`Total ${section.title} (HT): ${formatPrice(section.total)}`, 20, yPosition);
    yPosition += 20;
  });

  // Calcul de la TVA et du montant TTC
  const totalHT = calculatedEstimate.min; // Le montant calculé est déjà TTC
  const totalHT_corrige = totalHT / 1.1; // On retire la TVA pour avoir le vrai montant HT
  const tvaAmount = totalHT_corrige * 0.10; // Calcul de la TVA sur le montant HT
  const totalTTC = totalHT_corrige + tvaAmount; // Calcul du TTC final

  // ✅ **Cartouche récapitulatif amélioré**
  const boxHeight = 45; // **Hauteur du cartouche récapitulatif**
  
  // Vérifier s'il reste suffisamment d'espace pour le cartouche
  if (yPosition + boxHeight + 20 > 280) { // 20px de marge supplémentaire pour éviter toute troncature
    doc.addPage();
    yPosition = 20; // Repositionner en haut de la nouvelle page
  }
  
  const boxX = 115; // Aligné à droite
  const boxY = yPosition + 10;
  const boxWidth = 80;  // **Plus large pour éviter de rogner**

  doc.setFillColor(...backgroundGray);
  doc.rect(boxX, boxY, boxWidth, boxHeight, 'F'); // **Plus d'arrondis marqués**
  doc.setDrawColor(200, 200, 200);  // **Bordure fine**
  doc.rect(boxX, boxY, boxWidth, boxHeight); // **Ajout d'une bordure fine pour bien encadrer**

  doc.setFontSize(12);
  doc.setTextColor(...primaryColor);
  doc.text('Récapitulatif financier', boxX + 5, boxY + 7);

  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  doc.text(`Montant total HT : ${formatPrice(totalHT_corrige)}`, boxX + 5, boxY + 15);
  doc.setFont(undefined, 'italic');
  doc.text(`TVA (10%) : ${formatPrice(tvaAmount)}`, boxX + 5, boxY + 22);

  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...highlightColor);
  doc.text(`Montant total TTC : ${formatPrice(totalTTC)}`, boxX + 5, boxY + 30);

  // Pied de page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('Devis généré par SimulReno - Valable 30 jours.', 15, 285);
    doc.text(`Page ${i} / ${pageCount}`, 185, 285);
  }

  doc.save('devis-simulreno.pdf');
};



  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!calculatedEstimate || !calculatedEstimate.sections || calculatedEstimate.sections.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">
          Aucune donnée n'a été saisie. Veuillez compléter au moins une section pour générer une estimation.
        </p>
        <button onClick={onBack} className="btn btn-secondary">
          Retour
        </button>
      </div>
    );
  }

  return (
    <StepContainer
      title="Résultat de l'estimation"
      description="Récapitulatif détaillé des coûts"
      onNext={onReset}
      onBack={onBack}
      nextLabel="Nouvelle estimation"
    >
      <div className="space-y-6">
        {/* Informations du bien */}
        <div className="bg-white rounded-lg border p-6 space-y-4">
          <h3 className="text-xl font-semibold mb-4">Informations sur le bien</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <MapPin className="h-5 w-5 text-neutral-500" />
              <div>
                <p className="text-sm text-neutral-500">Adresse</p>
                <p className="font-medium">
                  {data?.description?.location?.address || 'Non renseigné'}
                </p>
                <p className="text-sm">
                  {data?.description?.location?.postcode} {data?.description?.location?.city}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Building2 className="h-5 w-5 text-neutral-500" />
              <div>
                <p className="text-sm text-neutral-500">Type de bien</p>
                <p className="font-medium">
                  {data?.description?.propertyType ? 
                    PROPERTY_TYPE_TRANSLATIONS[data.description.propertyType as keyof typeof PROPERTY_TYPE_TRANSLATIONS] 
                    : 'Non renseigné'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Ruler className="h-5 w-5 text-neutral-500" />
              <div>
                <p className="text-sm text-neutral-500">Surface</p>
                <p className="font-medium">
                  {data?.description?.area ? `${data.description.area} m²` : 'Non renseigné'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Estimation globale */}
        <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Estimation globale</h3>
          <div className="space-y-2">
            <p className="text-2xl font-bold text-primary">
              Total HT : {formatPrice(calculatedEstimate.min, false)}
            </p>
            <p className="text-xl font-semibold text-primary">
              Total TTC : {formatPrice(calculatedEstimate.min)}
            </p>
            <p className="text-sm text-neutral-600">
              TVA applicable : 10% (taux réduit travaux de rénovation)
            </p>
          </div>
          <p className="mt-4 text-sm text-neutral-600">
            Cette estimation est basée sur les prix moyens du marché et peut varier selon les spécificités de votre projet.
          </p>
        </div>

        {/* Détail par section */}
        {calculatedEstimate.sections.map((section) => (
          <div key={section.id} className="bg-white rounded-lg border p-6">
            <h3 className="text-xl font-semibold mb-4">{section.title}</h3>
            <div className="space-y-4">
              {section.items.map((item, index) => (
                <div key={index} className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-neutral-600">{item.details}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-neutral-600">HT : {formatPrice(item.price, false)}</p>
                    <p className="font-semibold">TTC : {formatPrice(item.price)}</p>
                  </div>
                </div>
              ))}
              <div className="pt-4 border-t mt-4">
                <div className="flex justify-between">
                  <span className="font-medium">Total de la section</span>
                  <div className="text-right">
                    <p className="text-sm text-neutral-600">HT : {formatPrice(section.total, false)}</p>
                    <p className="font-semibold text-primary">TTC : {formatPrice(section.total)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="flex flex-col space-y-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3"
          >
            {saving ? 'Enregistrement...' : (user ? 'Enregistrer cette estimation' : 'Créer un compte pour sauvegarder')}
          </button>

          <div className="flex space-x-4">
            <button
              onClick={handleExport}
              className="flex-1 btn btn-secondary flex items-center justify-center"
            >
              <Download className="h-5 w-5 mr-2" />
              Exporter en PDF
            </button>
          </div>
        </div>

        {error && (
          <div className="text-red-600 text-center p-4 bg-red-50 rounded-lg">
            {error}
          </div>
        )}
      </div>
    </StepContainer>
  );
};

export default ResultStep;